import { apiSlice } from './apiSlice'
import { ENDPOINTS } from '../../lib/endpoints'

export interface ChatAgentCategory {
  id: string
  name: string
  slug: string
  color: string
  icon: string
}

export interface ChatAgent {
  id: string
  name: string
  description: string
  categoryIds: string[]
  categories: ChatAgentCategory[]
  icon: string | null
  color: string | null
  status: string
}

export interface ChatAgentsResponse {
  agents: ChatAgent[]
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export interface ChatAgentParams {
  q?: string
  categories?: string[]
  limit?: number
  offset?: number
}

export interface ChatConversation {
  id: string
  type: 'ai' | 'user'
  agentId: string | null
  agentName: string | null
  lastMessage: {
    content: string
    senderType: 'user' | 'agent'
    createdAt: string
  } | null
  updatedAt: string
}

export interface ChatMessage {
  id: string
  senderType: 'user' | 'agent'
  senderId: string
  senderName: string | null
  content: string | null
  isDeleted: boolean
  editedAt: string | null
  createdAt: string
  isPremium: boolean
  premiumAmountPaise: number | null
  unlocked: boolean | null
}

export interface MyUnlockInvoice {
  id: string
  invoiceNumber: string
  issuedAt: string
  totalPaise: number
  isPlaceholder: boolean
  agentName: string
}

export const chatApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    getAgentById: builder.query<{ agent: ChatAgent }, string>({
      query: (id) => ENDPOINTS.chat.agent(id),
      providesTags: (_result, _err, id) => [{ type: 'ChatAgent', id }],
    }),

    getChatAgents: builder.query<ChatAgentsResponse, ChatAgentParams | void>({
      query: (params) => {
        const p = (params ?? {}) as ChatAgentParams
        const qs = new URLSearchParams()
        if (p.q) qs.set('q', p.q)
        if (p.categories?.length) p.categories.forEach(c => qs.append('categories[]', c))
        if (p.limit)  qs.set('limit',  String(p.limit))
        if (p.offset) qs.set('offset', String(p.offset))
        const str = qs.toString()
        return str ? `${ENDPOINTS.chat.agents}?${str}` : ENDPOINTS.chat.agents
      },
      providesTags: ['ChatAgent'],
    }),

    getChatCategories: builder.query<{ categories: ChatAgentCategory[] }, void>({
      query: () => ENDPOINTS.chat.categories,
      providesTags: ['ChatAgent'],
    }),

    getConversations: builder.query<{ conversations: ChatConversation[] }, void>({
      query: () => ENDPOINTS.chat.conversations,
      providesTags: ['ChatConversation'],
    }),

    getOrCreateConversation: builder.mutation<{ conversation: { id: string; type: string; participants: any[] } }, { agentId: string }>({
      query: (body) => ({ url: ENDPOINTS.chat.conversations, method: 'POST', body }),
      invalidatesTags: ['ChatConversation'],
    }),

    getMessages: builder.query<{ data: ChatMessage[]; nextCursor: string | null; hasMore: boolean; freeUnlocksRemaining: number | null }, { conversationId: string; cursor?: string }>({
      query: ({ conversationId, cursor }) => ({
        url: cursor
          ? `${ENDPOINTS.chat.messages(conversationId)}?cursor=${encodeURIComponent(cursor)}`
          : ENDPOINTS.chat.messages(conversationId),
      }),
      providesTags: (_result, _err, { conversationId }) => [{ type: 'ChatMessage', id: conversationId }],
    }),

    sendMessage: builder.mutation<{ message: ChatMessage }, { conversationId: string; content: string }>({
      query: ({ conversationId, content }) => ({
        url: ENDPOINTS.chat.messages(conversationId),
        method: 'POST',
        body: { content },
      }),
      invalidatesTags: (_result, _err, { conversationId }) => [
        { type: 'ChatMessage', id: conversationId },
        'ChatConversation',
      ],
    }),

    deleteMessage: builder.mutation<void, { conversationId: string; messageId: string }>({
      query: ({ conversationId, messageId }) => ({
        url: ENDPOINTS.chat.deleteMessage(conversationId, messageId),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _err, { conversationId }) => [{ type: 'ChatMessage', id: conversationId }],
    }),

    createMessageUnlockOrder: builder.mutation<
      { orderId: string; amountPaise: number; currency: string; keyId: string },
      { conversationId: string; messageId: string }
    >({
      query: ({ conversationId, messageId }) => ({
        url: ENDPOINTS.chat.unlockOrder(conversationId, messageId),
        method: 'POST',
      }),
    }),

    unlockMessage: builder.mutation<
      { unlockId: string; content: string },
      { conversationId: string; messageId: string; razorpayOrderId: string; razorpayPaymentId: string; razorpaySignature: string }
    >({
      query: ({ conversationId, messageId, ...body }) => ({
        url: ENDPOINTS.chat.unlockMessage(conversationId, messageId),
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _err, { conversationId }) => [{ type: 'ChatMessage', id: conversationId }],
    }),

    // Free trial unlock for a webhook (external) agent's premium reply — no
    // payment involved. See freeUnlocksRemaining on getMessages.
    unlockMessageFree: builder.mutation<
      { unlockId: string; content: string },
      { conversationId: string; messageId: string }
    >({
      query: ({ conversationId, messageId }) => ({
        url: ENDPOINTS.chat.unlockFree(conversationId, messageId),
        method: 'POST',
      }),
      invalidatesTags: (_result, _err, { conversationId }) => [{ type: 'ChatMessage', id: conversationId }],
    }),

    // Tax invoices for premium replies the current user has paid to unlock.
    getMyUnlockInvoices: builder.query<{ invoices: MyUnlockInvoice[] }, void>({
      query: () => ENDPOINTS.chat.unlockInvoices,
      keepUnusedDataFor: 0,
    }),

    // Tax invoices for premium discussion comments the user has paid to unlock.
    getMyCommentUnlockInvoices: builder.query<{ invoices: MyUnlockInvoice[] }, void>({
      query: () => ENDPOINTS.comments.unlockInvoices,
      extraOptions: { service: 'comments' },
      keepUnusedDataFor: 0,
    }),

  }),
})

export const {
  useGetMyUnlockInvoicesQuery,
  useGetMyCommentUnlockInvoicesQuery,
  useGetAgentByIdQuery,
  useGetChatAgentsQuery,
  useGetChatCategoriesQuery,
  useGetConversationsQuery,
  useGetOrCreateConversationMutation,
  useGetMessagesQuery,
  useSendMessageMutation,
  useDeleteMessageMutation,
  useCreateMessageUnlockOrderMutation,
  useUnlockMessageMutation,
  useUnlockMessageFreeMutation,
} = chatApi
